'use client';

import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import type { ComposerRevertReason } from '~/constants/composer';
import useComposerReadyState from '~/hooks/common/composer/useComposerReadyState';
import type { DeepPartial } from '~/types';
import { logInfo } from '~/utilities/loggers';

// Started once ComposerUiMfe's own onComponentLoaded fires (component successfully
// mounted), not from provider mount — mounting and signaling ready are separate,
// sequential events, so racing this against ComposerUiMfe's 10s mount-timeout from
// the same starting instant would erode the safety margin on slow-but-successful
// loads (mounts at t=9s, only ~3s left to signal ready). This timer only ever
// guards "mounted but never went ready".
const COMPOSER_READY_TIMEOUT_MS = 12000;

export type ContextType = {
  assignedBucket: string | undefined;
  isComposerReady: boolean;
  markComponentMounted: () => void;
  revertToControl: (reason: ComposerRevertReason) => void;
  showComposerTreatment: boolean;
};

export const defaultContext: ContextType = {
  assignedBucket: undefined,
  isComposerReady: false,
  markComponentMounted: () => {},
  revertToControl: () => {},
  showComposerTreatment: false
};

const ComposerContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  assignedBucket: string | undefined;
  showComposerTreatment: boolean;
}>;

export function ComposerContextProvider({
  assignedBucket,
  children,
  showComposerTreatment: initialShowComposerTreatment
}: OwnProps) {
  const rawIsComposerReady = useComposerReadyState();
  const [showComposerTreatment, setShowComposerTreatment] = useState(
    initialShowComposerTreatment
  );
  const [hasEverBeenReady, setHasEverBeenReady] = useState(false);
  const [hasComponentMounted, setHasComponentMounted] = useState(false);
  const hasComponentMountedRef = useRef(false);
  const hasEverBeenReadyRef = useRef(false);
  const hasRevertedRef = useRef(false);

  const markComponentMounted = useCallback(() => {
    hasComponentMountedRef.current = true;
    setHasComponentMounted(true);
  }, []);

  const revertToControl = useCallback(
    (reason: ComposerRevertReason) => {
      if (hasRevertedRef.current) return;

      hasRevertedRef.current = true;
      setShowComposerTreatment(false);

      logInfo('Composer reverted to control experience', {
        assignedBucket,
        hasComponentMounted: hasComponentMountedRef.current,
        hasEverBeenReady: hasEverBeenReadyRef.current,
        reason
      });
    },
    [assignedBucket]
  );

  useEffect(() => {
    if (rawIsComposerReady) {
      hasEverBeenReadyRef.current = true;
      setHasEverBeenReady(true);
    }
  }, [rawIsComposerReady]);

  // If the MFE never signals ready the first time around (regardless of why —
  // never mounted, errored, or mounted but the app inside never initialized),
  // fall back to control rather than leaving the user stuck with a broken/blank
  // composer experience. Only guards the initial load — once composer has been
  // ready at least once, a later unready (e.g. a teardown/reinit blip) doesn't
  // re-arm this countdown, so an already-working session is never reverted.
  // Armed from hasComponentMounted (ComposerUiMfe's onComponentLoaded), not from
  // provider mount, so it only ever measures "mounted but never went ready" — a
  // slow-but-successful mount never eats into this window.
  useEffect(() => {
    if (
      !showComposerTreatment ||
      !hasComponentMounted ||
      rawIsComposerReady ||
      hasEverBeenReady
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(
      () => revertToControl('ready-timeout'),
      COMPOSER_READY_TIMEOUT_MS
    );

    return () => window.clearTimeout(timeoutId);
  }, [
    hasComponentMounted,
    hasEverBeenReady,
    rawIsComposerReady,
    revertToControl,
    showComposerTreatment
  ]);

  // Gated on showComposerTreatment so a revert (which can happen after
  // composer was genuinely ready once, e.g. a component-error/timeout) can't
  // leave downstream consumers (composer.pageContext effects, useComposerOpenReady)
  // believing composer is still ready — useComposerReadyState's own listener half
  // has no reset path for a revert we trigger ourselves, only for a real
  // COMPOSER_UNREADY event from the widget.
  const isComposerReady = rawIsComposerReady && showComposerTreatment;

  return (
    <ComposerContext.Provider
      value={{
        assignedBucket,
        isComposerReady,
        markComponentMounted,
        revertToControl,
        showComposerTreatment
      }}
    >
      {children}
    </ComposerContext.Provider>
  );
}

ComposerContextProvider.displayName = 'ComposerContextProvider';

export const generateComposerContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useComposerAssignedBucket() {
  return useContext(ComposerContext).assignedBucket;
}

export function useIsComposerReady() {
  return useContext(ComposerContext).isComposerReady;
}

export function useMarkComposerComponentMounted() {
  return useContext(ComposerContext).markComponentMounted;
}

export function useRevertComposerTreatment() {
  return useContext(ComposerContext).revertToControl;
}

export function useShowComposerTreatment() {
  return useContext(ComposerContext).showComposerTreatment;
}

export function MockedComposerContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <ComposerContext.Provider value={generateComposerContext(mockValue)}>
      {children}
    </ComposerContext.Provider>
  );
}

export default ComposerContext;
